import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    LIFECYCLE_STATUS_MAP,
    HEALTH_STATUS,
    HEALTH_STATUS_MAP,
    AD_HEALTH_STATUS_LABEL_OVERRIDES,
} from 'common/constants/lifecycle-and-health-mappings';
import { Indicator } from './indicator';

export function getAdIndicatorSpec({ status, health, showHealth, overrideDefaultLabel }) {
    const statusSpec = LIFECYCLE_STATUS_MAP[status];
    const safeStatusSpec = statusSpec || {
        label: String(status || 'Unknown'),
        tone: 'neutral',
        variant: 'filled',
        Icon: undefined,
    };

    if (!showHealth || !health) return safeStatusSpec;

    // const allowed = VALID_HEALTH_BY_LIFECYCLE[status] || [];
    // if (!allowed.includes(health)) return safeStatusSpec;

    const base = HEALTH_STATUS_MAP[health];
    if (!base) return safeStatusSpec;

    // Optional label override to "... Ads"
    const override = AD_HEALTH_STATUS_LABEL_OVERRIDES?.[health];
    return override && overrideDefaultLabel ? { ...base, label: override } : base;
}

export function AdIndicator({
    status,
    health,
    size = 'small',
    count,
    variant,
    className,
    style,
    onClick,
    disabled = false,
    showHealth = false,
    overrideDefaultLabel = false,
}) {
    if (showHealth && health === HEALTH_STATUS.none) return null;

    const spec = useMemo(
        () => getAdIndicatorSpec({ status, health, showHealth, overrideDefaultLabel }),
        [status, health, showHealth]
    );

    const iconSize = size === 'small' ? 'small' : 'medium';
    const IconEl = spec && spec.Icon ? <spec.Icon fontSize={iconSize} /> : undefined;

    const isClickable = Boolean(showHealth && health && typeof onClick === 'function');

    return (
        spec && (
            <Indicator
                label={spec.label}
                tone={spec.tone}
                variant={variant || spec.variant || 'filled'}
                size={size}
                count={count}
                IconEl={IconEl}
                className={className}
                style={style}
                clickable={isClickable}
                onClick={
                    isClickable ? e => onClick(e, { entity: 'ad', status, health }) : undefined
                }
                disabled={isClickable ? disabled : undefined}
                ariaLabel={spec.label}
            />
        )
    );
}

AdIndicator.propTypes = {
    status: PropTypes.oneOf([...Object.keys(LIFECYCLE_STATUS_MAP)]),
    health: PropTypes.oneOf([...Object.keys(HEALTH_STATUS_MAP)]),
    size: PropTypes.oneOf(['small', 'medium']),
    count: PropTypes.number,
    variant: PropTypes.oneOf(['filled', 'outlined']),
    className: PropTypes.string,
    style: PropTypes.object,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    showHealth: PropTypes.bool,
};

export default AdIndicator;
